import {
  BLOCKS,
  INLINES,
  Document,
  Node as ContentfulNode,
  type Block,
  type Inline,
  type Text,
  type TopLevelBlock,
} from '@contentful/rich-text-types';
import { OPTIONAL_FIELDS_BY_TYPE, RICH_TEXT_ASSET_LINK_KEYS, RICH_TEXT_ENTRY_LINK_KEYS } from './constants';
import {
  FULL_SHORTCODE_INLINE_CONTEXT_KEYS,
  MINIMAL_HYPERLINK_ENTRY_CONTEXT_KEYS,
  RICH_TEXT_JSON_ONLY_KEYS,
} from './constants';
import {
  getTypenameForContentTypeId,
  isArrayOfEntries,
  isAsset,
  isDocument,
  isEntryWithSysAndSlug,
  isLink,
  isLocaleMapObject,
  isMediaAssetLike,
  toPascalCase,
  unwrapLocale,
} from './type-guards';
import type { AdaptRichTextContext, AdaptedRichText, IncludesMap } from './types';
import { getCollectionFieldName, getMappedFieldName } from './constants';
import { ENTRY_NATIVE_TYPENAMES } from 'utils/entryNativeTypenames';
import type { ModuleTypeName } from 'utils/modules';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getSysId(value: unknown): string | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const sys = value.sys;
  if (!isRecord(sys)) {
    return undefined;
  }

  return typeof sys.id === 'string' ? sys.id : undefined;
}

export function normalizeContentfulAssetUrl(url: string): string {
  return url.startsWith('//') ? `https:${url}` : url;
}

function isRawEntry(value: unknown): value is Record<string, unknown> {
  if (!isRecord(value)) {
    return false;
  }

  const fields = value.fields;
  if (!isRecord(fields)) {
    return false;
  }

  const sys = value.sys;
  if (!isRecord(sys)) {
    return false;
  }

  const contentType = sys.contentType;
  if (!isRecord(contentType)) {
    return false;
  }

  return isRecord(contentType.sys) && typeof contentType.sys.id === 'string';
}

export function resolveEntrySource(value: unknown, includesMap: IncludesMap): Record<string, unknown> | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  if (isLink(value)) {
    if (value.sys.linkType !== 'Entry') {
      return undefined;
    }

    const resolved = includesMap.entries.get(value.sys.id);
    return isRecord(resolved) ? resolved : undefined;
  }

  const sysId = getSysId(value);
  if (typeof sysId === 'string') {
    const resolved = includesMap.entries.get(sysId);
    if (isRawEntry(resolved)) {
      return resolved;
    }
  }

  return isRawEntry(value) ? value : undefined;
}

export function resolveAssetSource(value: unknown, includesMap: IncludesMap): Record<string, unknown> | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  if (isMediaAssetLike(value)) {
    return value;
  }

  if (isLink(value)) {
    if (value.sys.linkType !== 'Asset') {
      return undefined;
    }

    const resolved = includesMap.assets.get(value.sys.id);
    return isRecord(resolved) ? resolved : undefined;
  }

  const sysId = getSysId(value);
  if (typeof sysId === 'string') {
    const resolved = includesMap.assets.get(sysId);
    if (isRecord(resolved)) {
      return resolved;
    }
  }

  return isAsset(value) ? value : undefined;
}

/**
 * Extracts embedded entry references from a Document.
 * Returns sys.ids of all embedded-entry-inline and embedded-entry-block nodes.
 */
function extractEmbeddedEntryIds(doc: Document): { inline: string[]; block: string[]; hyperlink: string[] } {
  const inline: string[] = [];
  const block: string[] = [];
  const hyperlink: string[] = [];

  function traverse(node: ContentfulNode | Document): void {
    if ('nodeType' in node) {
      if (node.nodeType === 'embedded-entry-inline' && node.data?.target?.sys?.id) {
        inline.push(node.data.target.sys.id);
      }
      if (node.nodeType === 'embedded-entry-block' && node.data?.target?.sys?.id) {
        block.push(node.data.target.sys.id);
      }
      if (node.nodeType === 'entry-hyperlink' && node.data?.target?.sys?.id) {
        hyperlink.push(node.data.target.sys.id);
      }
    }

    if ('content' in node && Array.isArray(node.content)) {
      node.content.forEach(child => traverse(child));
    }
  }

  traverse(doc);
  return { inline, block, hyperlink };
}

function extractEmbeddedAssetBlockIds(doc: Document): string[] {
  const block: string[] = [];

  function traverse(node: ContentfulNode | Document): void {
    if ('nodeType' in node) {
      if (node.nodeType === 'embedded-asset-block' && node.data?.target?.sys?.id) {
        block.push(node.data.target.sys.id);
      }
    }

    if ('content' in node && Array.isArray(node.content)) {
      node.content.forEach(child => traverse(child));
    }
  }

  traverse(doc);
  return block;
}

const RICH_TEXT_TARGET_NODE_TYPES = new Set<string>([
  INLINES.EMBEDDED_ENTRY,
  BLOCKS.EMBEDDED_ENTRY,
  INLINES.ENTRY_HYPERLINK,
  BLOCKS.EMBEDDED_ASSET,
]);

const RICH_TEXT_INLINE_NODE_TYPES = new Set<string>(Object.values(INLINES));

function isTextNode(node: Block | Inline | Text): node is Text {
  return node.nodeType === 'text';
}

function isInlineNode(node: Block | Inline): node is Inline {
  return RICH_TEXT_INLINE_NODE_TYPES.has(node.nodeType);
}

function isStrippedRichTextTarget(value: unknown, id: string): boolean {
  if (!isRecord(value) || Object.keys(value).length !== 1) {
    return false;
  }

  const sys = value.sys;
  return isRecord(sys) && Object.keys(sys).length === 1 && sys.id === id;
}

function stripTargetFromNodeData(nodeType: string, data: Record<string, unknown>): Record<string, unknown> {
  if (!RICH_TEXT_TARGET_NODE_TYPES.has(nodeType)) {
    return data;
  }

  const targetId = getSysId(data.target);
  if (typeof targetId !== 'string' || isStrippedRichTextTarget(data.target, targetId)) {
    return data;
  }

  return {
    ...data,
    target: {
      sys: {
        id: targetId,
      },
    },
  };
}

function stripInlineTargets(node: Inline): Inline {
  const nextData = stripTargetFromNodeData(node.nodeType, node.data);
  let contentChanged = false;

  const nextContent = node.content.map(child => {
    if (isTextNode(child)) {
      return child;
    }

    const strippedChild = stripInlineTargets(child);
    if (strippedChild !== child) {
      contentChanged = true;
    }

    return strippedChild;
  });

  const dataChanged = nextData !== node.data;
  if (!dataChanged && !contentChanged) {
    return node;
  }

  return {
    ...node,
    data: nextData,
    content: contentChanged ? nextContent : node.content,
  };
}

function stripBlockTargets(node: Block): Block {
  const nextData = stripTargetFromNodeData(node.nodeType, node.data);
  let contentChanged = false;

  const nextContent = node.content.map(child => {
    if (isTextNode(child)) {
      return child;
    }

    const strippedChild = isInlineNode(child) ? stripInlineTargets(child) : stripBlockTargets(child);
    if (strippedChild !== child) {
      contentChanged = true;
    }

    return strippedChild;
  });

  const dataChanged = nextData !== node.data;
  if (!dataChanged && !contentChanged) {
    return node;
  }

  return {
    ...node,
    data: nextData,
    content: contentChanged ? nextContent : node.content,
  };
}

function stripTopLevelBlockTargets(node: TopLevelBlock): TopLevelBlock {
  const nextData = stripTargetFromNodeData(node.nodeType, node.data);
  let contentChanged = false;

  const nextContent = node.content.map(child => {
    if (isTextNode(child)) {
      return child;
    }

    const strippedChild = isInlineNode(child) ? stripInlineTargets(child) : stripBlockTargets(child);
    if (strippedChild !== child) {
      contentChanged = true;
    }

    return strippedChild;
  });

  const dataChanged = nextData !== node.data;
  if (!dataChanged && !contentChanged) {
    return node;
  }

  return {
    ...node,
    data: nextData,
    content: contentChanged ? nextContent : node.content,
  };
}

function stripDocumentTargets(doc: Document): Document {
  let contentChanged = false;

  const nextContent = doc.content.map(block => {
    const strippedBlock = stripTopLevelBlockTargets(block);
    if (strippedBlock !== block) {
      contentChanged = true;
    }

    return strippedBlock;
  });

  if (!contentChanged) {
    return doc;
  }

  return {
    ...doc,
    content: nextContent,
  };
}

/**
 * Transforms a REST API Asset to MediaAsset format.
 * Flattens the nested structure and unwraps locale-specific fields.
 */
export function adaptAsset(asset: unknown): Record<string, unknown> {
  if (typeof asset !== 'object' || asset === null) {
    return {};
  }

  const assetObj = asset as Record<string, unknown>;
  const sys = assetObj.sys as Record<string, unknown> | undefined;
  const fields = assetObj.fields as Record<string, unknown> | undefined;

  if (!fields) {
    return {};
  }

  // Extract title and description (may be locale-wrapped)
  const title = unwrapLocale<string>(fields.title) ?? '';
  const description = unwrapLocale<string>(fields.description) ?? null;

  // Extract file information
  // With link resolution enabled, file fields may be locale-wrapped
  const rawFile = fields.file as Record<string, unknown> | undefined;
  const file =
    (rawFile && isLocaleMapObject(rawFile) ? unwrapLocale<Record<string, unknown>>(rawFile) : rawFile) ?? undefined;

  if (!file || typeof file !== 'object') {
    return { title, description };
  }

  // Extract file properties directly (no locale unwrapping needed)
  const url = normalizeContentfulAssetUrl((file.url as string | undefined) ?? '');
  const contentType = (file.contentType as string | undefined) ?? '';
  const fileName = (file.fileName as string | undefined) ?? null;

  // Extract dimensions from file.details.image
  const details = file.details as Record<string, unknown> | undefined;
  const image = details?.image as Record<string, unknown> | undefined;
  const width = (image?.width as number | undefined) ?? null;
  const height = (image?.height as number | undefined) ?? null;
  const size = (details?.size as number | undefined) ?? null;

  // Return flattened MediaAsset-compatible object
  const result: Record<string, unknown> = {
    sys: sys && typeof sys.id === 'string' ? { id: sys.id } : undefined,
    url,
    title,
    contentType,
    description,
    fileName,
    width,
    height,
    size,
  };

  return result;
}

/**
 * Ensures optional fields are set to null instead of undefined to match GraphQL behavior.
 * GraphQL always includes optional fields as null, while REST omits them entirely.
 */
function ensureOptionalFields(adapted: Record<string, unknown>, typename: string): void {
  const optionalFields = OPTIONAL_FIELDS_BY_TYPE[typename];
  if (!optionalFields) {
    return;
  }

  for (const field of optionalFields) {
    if (!(field in adapted)) {
      adapted[field] = null;
    }
  }
}

/**
 * Cache for adapted entries to avoid re-processing and return consistent results.
 *
 * IMPORTANT: this cache must be reset per `adaptRESTtoPageProps` call because
 * the adapter performs additional normalization/mutations after `adaptEntry`.
 * Tests (and some call sites) reuse the same fixture objects across calls.
 *
 * Caches are split by `preserveEntryNativeModules` because allowlisted
 * typenames adapt to a different shape in each mode. Non-allowlisted typenames
 * adapt identically in either mode, so their result is dual-written to both
 * buckets — this prevents cross-mode lookup misses from falling through to the
 * `processed` cycle-stub path (which returns `{__typename, sys}` and crashes
 * downstream consumers like rich-text hyperlink resolution).
 */
interface AdaptedCaches {
  byObject: WeakMap<object, unknown>;
  byId: Map<string, unknown>;
}

function createAdaptedCaches(): AdaptedCaches {
  return { byObject: new WeakMap<object, unknown>(), byId: new Map<string, unknown>() };
}

let legacyAdaptedCaches: AdaptedCaches = createAdaptedCaches();
let preservedAdaptedCaches: AdaptedCaches = createAdaptedCaches();

function getAdaptedCaches(preserveEntryNativeModules: boolean): AdaptedCaches {
  return preserveEntryNativeModules ? preservedAdaptedCaches : legacyAdaptedCaches;
}

export function resetAdaptedCache(): void {
  legacyAdaptedCaches = createAdaptedCaches();
  preservedAdaptedCaches = createAdaptedCaches();
}

/**
 * Returns true if any nested object in `value` carries a `__typename` listed in
 * `ENTRY_NATIVE_TYPENAMES`. Used to gate the non-allowlisted dual-write: a
 * flattened parent is only safe to share across cache buckets when its entire
 * subtree adapts identically in both modes. Allowlisted descendants adopt a
 * different shape per mode (preserved Entry vs. flattened), so a parent that
 * embeds one cannot be dual-written.
 */
function containsAllowlistedDescendant(value: unknown, seen: WeakSet<object> = new WeakSet()): boolean {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  if (seen.has(value)) {
    return false;
  }
  seen.add(value);

  if (Array.isArray(value)) {
    for (const item of value) {
      if (containsAllowlistedDescendant(item, seen)) {
        return true;
      }
    }
    return false;
  }

  const record = value as Record<string, unknown>;
  const typename = record.__typename;
  if (typeof typename === 'string' && ENTRY_NATIVE_TYPENAMES.has(typename as ModuleTypeName)) {
    return true;
  }
  for (const key of Object.keys(record)) {
    if (containsAllowlistedDescendant(record[key], seen)) {
      return true;
    }
  }
  return false;
}

/**
 * Detects whether the raw `fields.nt_experiences` indicates active experiments.
 * Mirrors `annotateModule` for the entry-native preservation path, where
 * experiments still live nested under `fields` instead of at the top level.
 */
function hasEntryNativeExperiments(fields: Record<string, unknown>): boolean {
  const raw = fields.nt_experiences;
  if (Array.isArray(raw)) {
    return raw.length > 0;
  }
  if (raw && typeof raw === 'object') {
    const items = (raw as { items?: unknown }).items;
    if (Array.isArray(items)) {
      return items.length > 0;
    }
  }
  return false;
}

/**
 * Transforms a REST Entry to an adapted object with __typename.
 * Uses includes map to resolve Link objects without recursion.
 *
 * When `preserveEntryNativeModules` is true, entries whose typename is in
 * `ENTRY_NATIVE_TYPENAMES` short-circuit the flatten step and return a
 * preserved Entry shape — raw `sys`, raw `fields`, and existing entry metadata
 * intact, with top-level `__typename` and `hasExperiments` added so module
 * dispatch (via `moduleRegistry`) and Ninetailed discovery still work.
 */
export function adaptEntry(
  entry: unknown,
  includesMap: IncludesMap,
  processed: WeakSet<object> = new WeakSet(),
  wrapCollections: boolean = true,
  bypassCache: boolean = false,
  preserveEntryNativeModules: boolean = false,
): unknown {
  if (entry === null || entry === undefined) {
    return entry;
  }

  if (typeof entry !== 'object') {
    return entry;
  }

  // Handle arrays
  if (Array.isArray(entry)) {
    // Check for circular array reference
    if (processed.has(entry)) {
      return [];
    }
    processed.add(entry);
    return entry.map(item =>
      adaptEntry(item, includesMap, processed, wrapCollections, false, preserveEntryNativeModules),
    );
  }

  const entryObj = entry as Record<string, unknown>;

  const resolvedAsset = resolveAssetSource(entryObj, includesMap);
  if (resolvedAsset) {
    return isMediaAssetLike(resolvedAsset) ? resolvedAsset : adaptAsset(resolvedAsset);
  }

  const caches = getAdaptedCaches(preserveEntryNativeModules);

  // Check if we've already adapted this entry - if so, return the cached result
  if (!bypassCache && caches.byObject.has(entryObj)) {
    return caches.byObject.get(entryObj);
  }

  // With link resolution enabled, the SDK may return the same logical entry (same sys.id)
  // as multiple object instances at different points in the graph. byObject (WeakMap)
  // handles identical object instances. byId catches distinct object instances
  // with the same sys.id.
  const sysId = getSysId(entryObj);
  if (!bypassCache && typeof sysId === 'string' && caches.byId.has(sysId)) {
    return caches.byId.get(sysId);
  }

  // Check for circular object reference (currently being processed)
  if (processed.has(entryObj)) {
    // Return a minimal reference to break the cycle
    const sys = entryObj.sys as Record<string, unknown> | undefined;
    const entryId = sys?.id;
    if (typeof entryId === 'string') {
      const contentType = sys?.contentType as Record<string, unknown> | undefined;
      const contentTypeSys = contentType?.sys as Record<string, unknown> | undefined;
      const contentTypeId = contentTypeSys?.id as string | undefined;
      const __typename = contentTypeId ? toPascalCase(contentTypeId) : undefined;

      const fields = entryObj.fields as Record<string, unknown> | undefined;
      const slug = fields?.slug;

      // Prefer a "minimal Page-like reference" when available: { __typename, sys, slug }
      // This helps parity for link targets in cyclic graphs (e.g. footer links that point to pages with shared footer).
      if (typeof slug === 'string') {
        return __typename ? { __typename, sys: { id: entryId }, slug } : { sys: { id: entryId }, slug };
      }

      return __typename ? { __typename, sys: { id: entryId } } : { sys: { id: entryId } };
    }
    return {};
  }

  // Mark this object as being processed
  processed.add(entryObj);

  const resolvedEntry = resolveEntrySource(entryObj, includesMap);
  if (resolvedEntry && resolvedEntry !== entryObj) {
    return adaptEntry(resolvedEntry, includesMap, processed, wrapCollections, false, preserveEntryNativeModules);
  }

  // Check if this is a Link object
  if (isLink(entryObj)) {
    return { sys: { id: entryObj.sys.id } };
  }

  // Check if this is a Contentful Entry (has sys.contentType)
  const sys = entryObj.sys as Record<string, unknown> | undefined;
  const contentType = sys?.contentType as Record<string, unknown> | undefined;
  const contentTypeSys = contentType?.sys as Record<string, unknown> | undefined;
  const contentTypeId = contentTypeSys?.id as string | undefined;

  const isEntry = contentTypeId !== undefined;

  if (isEntry && entryObj.fields) {
    // This is a Contentful Entry, flatten it
    const fields = entryObj.fields as Record<string, unknown>;
    const __typename = getTypenameForContentTypeId(contentTypeId);

    // Entry-native pass-through. When the typename is allowlisted, return the
    // raw Entry shape (sys + fields intact) plus top-level `__typename` /
    // `hasExperiments` so the render and experimentation layers keep working.
    if (preserveEntryNativeModules && ENTRY_NATIVE_TYPENAMES.has(__typename as ModuleTypeName)) {
      const preserved: Record<string, unknown> = {
        ...entryObj,
        __typename,
        hasExperiments: hasEntryNativeExperiments(fields),
      };
      caches.byObject.set(entryObj, preserved);
      if (typeof sys?.id === 'string') {
        caches.byId.set(sys.id as string, preserved);
      }
      return preserved;
    }

    const adapted: Record<string, unknown> = {
      sys: { id: sys?.id },
      __typename,
    };

    // Extract metadata from entry and map to contentfulMetadata
    const metadata = entryObj.metadata as
      | { tags?: Array<{ sys: { id: string } }>; concepts?: Array<{ sys: { id: string } }> }
      | undefined;
    adapted.contentfulMetadata = {
      tags: metadata?.tags?.map(tag => ({ id: tag.sys.id })) ?? [],
      concepts: metadata?.concepts?.map(concept => ({ id: concept.sys.id })) ?? [],
    };

    // Process each field
    for (const [key, value] of Object.entries(fields)) {
      // Apply field alias mapping (REST field name -> GraphQL alias)
      // Pass typename for type-specific mappings
      const mappedKey = getMappedFieldName(key, __typename);

      // Recorded fixtures: MediaCopy sometimes includes sliderEntryCollection (empty) even when the REST entry only has imageAspectRatio.
      // Treat imageAspectRatio as a signal to include an empty sliderEntryCollection, but do not expose imageAspectRatio in output.
      if (__typename === 'MediaCopy' && key === 'imageAspectRatio') {
        if (!('sliderEntryCollection' in adapted)) {
          adapted.sliderEntryCollection = { items: [] };
        }
        continue;
      }

      // Recorded fixtures: MediaCopy exposes `eyebrowChimeLogo` derived from `showChimeLogoInEyebrow` when present.
      if (__typename === 'MediaCopy' && key === 'showChimeLogoInEyebrow') {
        const localeUnwrappedValue =
          typeof value === 'object' &&
          value !== null &&
          !Array.isArray(value) &&
          isLocaleMapObject(value as Record<string, unknown>)
            ? (unwrapLocale<unknown>(value) ?? value)
            : value;

        adapted.eyebrowChimeLogo = localeUnwrappedValue === true;
        continue;
      }

      if (value === null || value === undefined) {
        adapted[mappedKey] = value;
        continue;
      }

      const localeUnwrappedValue =
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        isLocaleMapObject(value as Record<string, unknown>)
          ? (unwrapLocale<unknown>(value) ?? value)
          : value;

      // Handle rich text Documents
      if (isDocument(localeUnwrappedValue)) {
        adapted[mappedKey] = adaptRichText(localeUnwrappedValue, includesMap, processed, {
          parentTypename: __typename,
          fieldName: mappedKey,
        });
        continue;
      }

      // Handle arrays
      if (Array.isArray(localeUnwrappedValue)) {
        const adaptedItems = localeUnwrappedValue.map(item =>
          adaptEntry(item, includesMap, processed, true, false, preserveEntryNativeModules),
        );

        // Check if this array should be wrapped in a collection structure
        // Arrays of entries/links get wrapped in {items: [...]} for nested entries
        // Page-level arrays are handled separately in adaptRESTtoPageProps
        if (wrapCollections && localeUnwrappedValue.length > 0 && isArrayOfEntries(localeUnwrappedValue)) {
          const collectionName = getCollectionFieldName(key, __typename);
          // Wrap in {items: [...]} structure to match GraphQL collections
          adapted[collectionName] = { items: adaptedItems };
        } else {
          adapted[mappedKey] = adaptedItems;
        }
        continue;
      }

      // CTA.internalPage: resolve shallowly to {sys, slug} without recursing into the page.
      // Fully traversing the linked page from a CTA creates a cycle: the page's nav/footer
      // links back to the current page, which re-traverses MediaCopy → CTA → cycle stub.
      // normalizeCtaLinks only uses {sys, slug} from internalPage anyway.
      if (__typename === 'Cta' && key === 'internalPage') {
        const rawPage = localeUnwrappedValue as Record<string, unknown>;
        const resolvedPage = resolveEntrySource(rawPage, includesMap) ?? rawPage;
        const pageSys = (resolvedPage as Record<string, unknown>)?.sys as Record<string, unknown> | undefined;
        const pageId = pageSys?.id as string | undefined;
        if (pageId) {
          const pageFields = (resolvedPage as Record<string, unknown>)?.fields as Record<string, unknown> | undefined;
          const rawSlug = pageFields?.slug;
          const slug =
            typeof rawSlug === 'string'
              ? rawSlug
              : rawSlug != null &&
                  typeof rawSlug === 'object' &&
                  !Array.isArray(rawSlug) &&
                  isLocaleMapObject(rawSlug as Record<string, unknown>)
                ? (unwrapLocale<string>(rawSlug as Record<string, unknown>) ?? null)
                : null;
          adapted.internalPage = slug != null ? { sys: { id: pageId }, slug } : { sys: { id: pageId } };
        } else {
          adapted.internalPage = null;
        }
        continue;
      }

      // Handle objects (including Links)
      if (typeof localeUnwrappedValue === 'object' && localeUnwrappedValue !== null) {
        adapted[mappedKey] = adaptEntry(
          localeUnwrappedValue,
          includesMap,
          processed,
          true,
          false,
          preserveEntryNativeModules,
        );
        continue;
      }

      // Primitive value - keep as-is
      adapted[mappedKey] = localeUnwrappedValue;
    }

    // Ensure optional fields are set to null instead of undefined to match GraphQL
    // This is important because GraphQL always includes optional fields as null
    ensureOptionalFields(adapted, __typename);

    // Cache the adapted result before returning. Non-allowlisted typenames adapt
    // to the same flattened shape in either mode, so write to both buckets to
    // keep cross-mode lookups (e.g. rich-text resolution running preserve=false
    // against a preserve=true root adapter) from falling through to the
    // `processed` cycle stub. Skip the dual-write when the subtree contains an
    // allowlisted descendant, since those children's shapes differ per mode and
    // would leak the wrong shape into the opposite bucket.
    caches.byObject.set(entryObj, adapted);
    if (typeof sys?.id === 'string') {
      caches.byId.set(sys.id as string, adapted);
    }
    if (!ENTRY_NATIVE_TYPENAMES.has(__typename as ModuleTypeName) && !containsAllowlistedDescendant(adapted)) {
      const otherCaches = caches === legacyAdaptedCaches ? preservedAdaptedCaches : legacyAdaptedCaches;
      otherCaches.byObject.set(entryObj, adapted);
      if (typeof sys?.id === 'string') {
        otherCaches.byId.set(sys.id as string, adapted);
      }
    }

    return adapted;
  }

  // Not a Contentful entry, process as regular object
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(entryObj)) {
    if (key === 'sys') {
      result[key] = value;
      continue;
    }

    if (Array.isArray(value)) {
      result[key] = value.map(item =>
        adaptEntry(item, includesMap, processed, wrapCollections, false, preserveEntryNativeModules),
      );
    } else if (typeof value === 'object' && value !== null) {
      result[key] = adaptEntry(value, includesMap, processed, wrapCollections, false, preserveEntryNativeModules);
    } else {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Adapts a rich text Document to include links structure.
 * Extracts embedded entry references and resolves them from includes.
 * Always includes links structure to match GraphQL, even when empty.
 */
export function adaptRichText(
  doc: Document,
  includesMap: IncludesMap,
  processed: WeakSet<object>,
  context?: AdaptRichTextContext,
): AdaptedRichText {
  const embeddedIds = extractEmbeddedEntryIds(doc);
  const embeddedAssetBlockIds = extractEmbeddedAssetBlockIds(doc);
  const uniqueInlineIds = Array.from(new Set(embeddedIds.inline));
  const uniqueBlockIds = Array.from(new Set(embeddedIds.block));
  const uniqueHyperlinkIds = Array.from(new Set(embeddedIds.hyperlink));
  const uniqueAssetBlockIds = Array.from(new Set(embeddedAssetBlockIds));

  const key = context ? `${context.parentTypename}.${context.fieldName}` : undefined;
  if (key && RICH_TEXT_JSON_ONLY_KEYS.has(key)) {
    return { json: stripDocumentTargets(doc) };
  }
  const requestedKeys = key ? RICH_TEXT_ENTRY_LINK_KEYS[key] : undefined;
  const requestedKeySet = requestedKeys ? new Set(requestedKeys) : undefined;

  const entryKeys = new Set<'inline' | 'block' | 'hyperlink'>(['inline']);
  if (requestedKeys) {
    for (const k of requestedKeys) {
      entryKeys.add(k);
    }
  }

  // If the document actually contains embedded entries for a category, include that key even if not requested explicitly.
  // BUT: if the GraphQL fragment explicitly excludes a key (via requestedKeys), we must not add it back.
  if (uniqueBlockIds.length > 0 && (!requestedKeySet || requestedKeySet.has('block'))) entryKeys.add('block');
  if (uniqueHyperlinkIds.length > 0 && (!requestedKeySet || requestedKeySet.has('hyperlink')))
    entryKeys.add('hyperlink');

  const entriesLinks: Record<string, unknown> = {};
  for (const k of entryKeys) {
    entriesLinks[k] = [];
  }

  // Always include links.entries structure (with only the keys we expect for this field)
  const links: Record<string, unknown> = {
    entries: entriesLinks,
  };

  if (key === 'Compare.content') {
    links.__typename = 'CompareContentLinks';
  }

  const assetKeys = key ? RICH_TEXT_ASSET_LINK_KEYS[key] : undefined;
  if (assetKeys && assetKeys.length > 0) {
    const assetsLinks: Record<string, unknown> = {};
    for (const k of assetKeys) {
      assetsLinks[k] = [];
    }
    links.assets = assetsLinks;
  }

  const entries = links.entries as Record<string, unknown>;
  const assets = links.assets as Record<string, unknown> | undefined;

  if (uniqueInlineIds.length > 0 && Array.isArray(entries.inline)) {
    // Resolve inline entries from includes
    entries.inline = uniqueInlineIds
      .map(id => {
        const resolved = includesMap.entries.get(id);
        if (resolved) {
          const adapted = adaptEntry(resolved, includesMap, processed);
          if (adapted && typeof adapted === 'object') {
            const adaptedObj = adapted as Record<string, unknown>;
            if (adaptedObj.__typename === 'Shortcode' && (!key || !FULL_SHORTCODE_INLINE_CONTEXT_KEYS.has(key))) {
              return { __typename: 'Shortcode' };
            }
            const minimalDisclosureWithoutItemsKeys = new Set([
              'HeroConsolidated.copy',
              'MediaCopy.copy',
              'CalloutIconCard.headlineSmallRte',
            ]);
            const minimalDisclosureWithItemsKeys = new Set([
              'CalloutIconCard.copy',
              'AccordionItem.accordionCopy',
              'ContentBlock.headline',
            ]);

            // Recorded fixtures: some rich text contexts embed Disclosures with a minimal fragment.
            if (
              key &&
              (minimalDisclosureWithoutItemsKeys.has(key) || minimalDisclosureWithItemsKeys.has(key)) &&
              adaptedObj.__typename === 'Disclosure'
            ) {
              if (minimalDisclosureWithItemsKeys.has(key)) {
                const hasDisclosureItem = 'disclosureItem' in adaptedObj;
                const hasDisclosureSymbol = 'disclosureSymbol' in adaptedObj;
                return {
                  sys: adaptedObj.sys,
                  __typename: 'Disclosure',
                  copy: adaptedObj.copy,
                  disclosureItem: hasDisclosureItem ? adaptedObj.disclosureItem : null,
                  disclosureSymbol: hasDisclosureSymbol ? adaptedObj.disclosureSymbol : null,
                  symbol: adaptedObj.symbol,
                };
              }
              return {
                sys: adaptedObj.sys,
                __typename: 'Disclosure',
                copy: adaptedObj.copy,
                symbol: adaptedObj.symbol,
              };
            }

            // Recorded fixtures: Disclosures embedded in CardsIconItem.cardCopy include disclosureItem/disclosureSymbol as null.
            if (key === 'CardsIconItem.cardCopy' && adaptedObj.__typename === 'Disclosure') {
              const hasDisclosureItem = 'disclosureItem' in adaptedObj;
              const hasDisclosureSymbol = 'disclosureSymbol' in adaptedObj;
              return {
                ...adaptedObj,
                disclosureItem: hasDisclosureItem ? adaptedObj.disclosureItem : null,
                disclosureSymbol: hasDisclosureSymbol ? adaptedObj.disclosureSymbol : null,
              };
            }

            // Recorded fixtures: Compare.content links include Disclosure items with disclosureItem/disclosureSymbol as null.
            if (key === 'Compare.content' && adaptedObj.__typename === 'Disclosure') {
              const hasDisclosureItem = 'disclosureItem' in adaptedObj;
              const hasDisclosureSymbol = 'disclosureSymbol' in adaptedObj;
              return {
                ...adaptedObj,
                disclosureItem: hasDisclosureItem ? adaptedObj.disclosureItem : null,
                disclosureSymbol: hasDisclosureSymbol ? adaptedObj.disclosureSymbol : null,
              };
            }
          }
          return adapted;
        }
        return null;
      })
      .filter(entry => entry !== null);
  }

  if (uniqueBlockIds.length > 0 && Array.isArray(entries.block)) {
    // Resolve block entries from includes
    entries.block = uniqueBlockIds
      .map(id => {
        const resolved = includesMap.entries.get(id);
        if (resolved) {
          return adaptEntry(resolved, includesMap, processed);
        }
        return null;
      })
      .filter(entry => entry !== null);
  }

  if (uniqueHyperlinkIds.length > 0 && Array.isArray(entries.hyperlink)) {
    entries.hyperlink = uniqueHyperlinkIds
      .map(id => {
        const resolved = includesMap.entries.get(id);
        if (resolved) {
          const adapted = adaptEntry(resolved, includesMap, processed);
          if (adapted && typeof adapted === 'object' && key && MINIMAL_HYPERLINK_ENTRY_CONTEXT_KEYS.has(key)) {
            const adaptedObj = adapted as Record<string, unknown>;
            if (isEntryWithSysAndSlug(adaptedObj)) {
              return { __typename: adaptedObj.__typename, sys: adaptedObj.sys, slug: adaptedObj.slug };
            }
          }
          return adapted;
        }
        return null;
      })
      .filter(entry => entry !== null);
  }

  if (assets && uniqueAssetBlockIds.length > 0 && Array.isArray(assets.block)) {
    assets.block = uniqueAssetBlockIds
      .map(id => {
        const resolved = includesMap.assets.get(id);
        if (!resolved) return null;

        const asset = adaptAsset(resolved);
        const sys = asset.sys;
        const sysRecord =
          sys && typeof sys === 'object' && !Array.isArray(sys) ? (sys as Record<string, unknown>) : undefined;
        const sysId = sysRecord?.id;

        return {
          sys: typeof sysId === 'string' ? { id: sysId } : undefined,
          url: asset.url,
          width: asset.width,
          height: asset.height,
          contentType: asset.contentType,
          title: asset.title,
          description: asset.description,
        };
      })
      .filter(a => a !== null);
  }

  return {
    json: stripDocumentTargets(doc),
    links,
  };
}
